// Claim a free agent onto a waiver queue. Optional same-position drop; the drop
// is required (and submit disabled) when adding would exceed a roster cap.
import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Player, RosterSlot } from '../../types';

interface ClaimPlayerModalProps {
  leagueId: number;
  teamId: number;
  addPlayer: Player;
  myRoster: RosterSlot[];
  rosterFull: boolean;      // 6 total
  positionFull: boolean;    // 3 at the add-player's position
  onClose: () => void;
  onSubmitted: () => void;
}

export default function ClaimPlayerModal({
  leagueId, teamId, addPlayer, myRoster, rosterFull, positionFull, onClose, onSubmitted,
}: ClaimPlayerModalProps) {
  const dropRequired = rosterFull || positionFull;
  const [dropId, setDropId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Same-position drop candidates only (mirrors the swap rule).
  const dropChoices = myRoster.filter((s) => s.player && s.player.position === addPlayer.position);
  const canSubmit = (!dropRequired || dropId !== '') && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiService.submitWaiverClaim(leagueId, {
        team_id: teamId,
        player_add_id: addPlayer.id,
        player_drop_id: dropId === '' ? undefined : Number(dropId),
      });
      onSubmitted();
    } catch (e: any) {
      setError(e.message || 'Failed to submit claim');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-ink-900/60" onClick={onClose} />
      <div className="relative bg-white border border-ink-900 w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-ink-900">
          <h2 className="font-display text-xl uppercase tracking-tight text-ink-900">Claim Player</h2>
          <button onClick={onClose} aria-label="Close" className="icon-btn p-2 text-secondary-400 hover:text-ink-900">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-secondary-700">
            Claiming <span className="font-semibold text-ink-900">{addPlayer.name}</span>
            <span className={`ml-2 px-1.5 py-0.5 text-[0.65rem] font-display leading-none ${
              addPlayer.position === 'K' ? 'bg-ink-900 text-gold' : 'bg-success text-white'}`}>{addPlayer.position}</span>
          </p>
          {dropRequired && (
            <p className="text-xs text-danger">
              Your {addPlayer.position === 'K' ? 'kicker' : 'punter'} slots are full — choose a
              {addPlayer.position === 'K' ? ' kicker' : ' punter'} to drop.
            </p>
          )}
          <div>
            <label htmlFor="waiver-drop" className="block text-xs font-semibold uppercase tracking-wider text-secondary-500 mb-1.5">
              Drop {dropRequired ? '(required)' : '(optional)'}
            </label>
            <select id="waiver-drop" value={dropId}
              onChange={(e) => setDropId(e.target.value === '' ? '' : Number(e.target.value))}
              className="form-select w-full">
              <option value="">{dropRequired ? 'Select a player…' : 'No drop'}</option>
              {dropChoices.map((s) => (
                <option key={s.id} value={s.player!.id}>{s.player!.name}</option>
              ))}
            </select>
          </div>
          {error && <p className="text-sm text-danger">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-secondary-200">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-secondary-300 text-secondary-600 hover:border-ink-900 hover:text-ink-900">Cancel</button>
          <button onClick={handleSubmit} disabled={!canSubmit}
            className="px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-ink-900 text-paper hover:bg-ink-900/90 disabled:opacity-50">
            {submitting ? 'Submitting…' : 'Submit Claim'}
          </button>
        </div>
      </div>
    </div>
  );
}
